import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from '../../../redux/headerSlice/selectors';
import { useMiaChat } from 'app/NewAIAvatarLayout/globalContexts/MiaChatContext';
import { ChatState, Message, FlowStep } from '../../../types';
import { Message as APIMessage, MiaChatEvent, GenerateEmailRequest, GenerateEmailResponse, ChatRequest } from '../types';
import { logger } from 'app/utils/logger';
import { miaChatV2SocketService } from '../socketService/miaChatSocketService';
import { MiaChatV2SocketService } from '../socketService/miaChatSocketService';
import { socketServiceManager } from 'app/socketService';
import MiaChatV2ApiService from '../apiService/miaChatApiService';

// Utility function to format error messages for network issues
const formatNetworkErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    
    // Check for specific network error patterns
    if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('failed to fetch')) {
      return "I'm experiencing a network connectivity issue. Please check your internet connection and try again. If the problem persists, it may be a temporary server issue.";
    }
    
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
      return "The request took too long to complete. This might be due to network latency or server load. Please try again in a moment.";
    }
    
    if (errorMessage.includes('502') || errorMessage.includes('bad gateway')) {
      return "The server is temporarily unavailable (Bad Gateway). This is usually a temporary issue. Please wait a moment and try again.";
    }
    
    if (errorMessage.includes('503') || errorMessage.includes('service unavailable')) {
      return "The service is temporarily unavailable. Please try again in a few moments.";
    }
    
    if (errorMessage.includes('504') || errorMessage.includes('gateway timeout')) {
      return "The request timed out while waiting for the server. Please try again.";
    }
    
    if (errorMessage.includes('500') || errorMessage.includes('internal server error')) {
      return "An internal server error occurred. Our team has been notified. Please try again in a moment.";
    }
    
    if (errorMessage.includes('no response') || errorMessage.includes('could not be reached')) {
      return "Unable to connect to the server. Please check your internet connection and try again. If the problem persists, it may be a temporary server issue.";
    }
    
    // Generic network error message
    if (errorMessage.includes('aborted') || error.name === 'AbortError') {
      return null; // Don't show error for aborted requests
    }
    
    // Return a user-friendly message for other errors
    return "I encountered an issue processing your request. Please try again. If the problem continues, it may be a temporary network or server issue.";
  }
  
  // Fallback for unknown error types
  return "I'm experiencing a connection issue. Please check your internet connection and try again.";
};

const createInitialChatState = (): ChatState => ({
  step: 0 as FlowStep,
  chatHistory: [],
  emailContent: { subject: '', sections: [] },
  activeTab: "Generate",
  showPreview: false,
  inputText: "",
  loading: false,
  error: null,
  suggestions: [],
  next_action: '',
  is_input_restricted_to_suggestions: false
});

export interface UseMiaChatInterfaceReturn {
  chatState: ChatState;
  conversationId: string | null;
  isLoading: boolean;
  error: string | null;
  progressCards: any[];
  isGeneratingEmail: boolean;
  isEmailGenerationModalOpen: boolean;
  streamingData: any;
  currentSection: { index: number; type: string; main?: string; sub_text?: string } | null;
  generatedEmail: any | null;
  isEmailPreviewOpen: boolean;
  isSocketConnected: boolean;
  isStreamingStalled: boolean;
  handleSendMessage: (message: string) => Promise<void>;
  handleOptionSelect: (option: { id: string; text: string }) => Promise<void>;
  handleReset: () => void;
  updateInputText: (text: string) => void;
  getSelectedOperation: () => string | undefined;
  openEmailPreview: () => void;
  closeEmailPreview: () => void;
  openEmailGenerationProgress: () => void;
  closeEmailGenerationProgress: () => void;
}

export const useMiaChatInterface = (
  view?: string,
  initialMessage?: string,
  onResetCallback?: () => void
): UseMiaChatInterfaceReturn => {
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';

  const {
    initChat,
    startNewChat,
    sendChatMessage,
    setLoading,
    setError,
  } = useMiaChat();

  const [chatState, setChatState] = useState<ChatState>(() => {
    const initialState = createInitialChatState();
    // If there's an initial message, add it to chat history
    if (initialMessage) {
      return {
        ...initialState,
        chatHistory: [{ role: 'user', content: initialMessage }],
        step: 1 as FlowStep,
      };
    }
    return initialState;
  });

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);
  const [progressCards, setProgressCards] = useState<any[]>([]);
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [isEmailGenerationModalOpen, setIsEmailGenerationModalOpen] = useState(false);
  const [hasAutoOpenedModal, setHasAutoOpenedModal] = useState(false); // Track if modal was auto-opened
  const [streamingData, setStreamingData] = useState<any>(null);
  const [currentSection, setCurrentSection] = useState<{ index: number; type: string; main?: string; sub_text?: string } | null>(null);
  const [generatedEmail, setGeneratedEmail] = useState<any | null>(null);
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(true);
  const [isStreamingStalled, setIsStreamingStalled] = useState(false);
  const emailGenerationCompleteRef = useRef<boolean>(false); // Track if email generation is complete

  const initChatAbortControllerRef = useRef<AbortController | null>(null);
  const hasInitializedRef = useRef(false);
  const socketServiceRef = useRef<MiaChatV2SocketService | null>(null);
  const subscriptionCallbackRef = useRef<((data: MiaChatEvent) => void) | null>(null);
  const chatHistoryRef = useRef<Message[]>([]);
  // Track last user message for retry functionality
  const lastUserMessageRef = useRef<{ text: string; optionId?: string } | null>(null);
  // Track last email generation request for retry functionality
  const lastEmailGenerationRef = useRef<{ messages: Message[]; conversationId: string } | null>(null);
  // Track streaming timeout
  const streamingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const streamingCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastStreamingUpdateTimeRef = useRef<number | null>(null);

  // Initialize socket service
  useEffect(() => {
    try {
      const defaultSocketUrl = process.env.REACT_APP_WS_URL || '';
      if (defaultSocketUrl) {
        const config = {
          url: defaultSocketUrl,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: Infinity, // Infinite attempts for backend restart
          reconnectionDelay: 1000,
          options: {
            path: '/ws/',
            transports: ['websocket', 'polling']
          }
        };
        
        socketServiceRef.current = miaChatV2SocketService(config);
        
        // CRITICAL: Register service with manager so onReconnect() is called on reconnect
        if (socketServiceRef.current) {
          socketServiceManager.registerService(socketServiceRef.current);
          
          // Monitor socket connection status
          const checkConnectionStatus = () => {
            const isConnected = socketServiceRef.current?.isConnected() ?? false;
            setIsSocketConnected(isConnected);
          };
          
          // Set up connection event listeners
          socketServiceRef.current.on('connect', () => {
            setIsSocketConnected(true);
            setIsStreamingStalled(false);
            logger.info('Socket connected - resuming streaming monitoring');
          });
          
          socketServiceRef.current.on('disconnect', () => {
            setIsSocketConnected(false);
            logger.info('Socket disconnected - will monitor for reconnect');
          });
          
          // Check connection status periodically
          const connectionCheckInterval = setInterval(checkConnectionStatus, 2000);
          
          // Initial check
          checkConnectionStatus();
          
          // Cleanup
          return () => {
            clearInterval(connectionCheckInterval);
            socketServiceRef.current?.off('connect', () => {});
            socketServiceRef.current?.off('disconnect', () => {});
          };
        }
        
        logger.info('Socket service initialized and registered');
      }
    } catch (error) {
      logger.error('Failed to initialize socket service', error);
    }
  }, []);

  // Helper function to get selected_operation based on view
  const getSelectedOperation = useCallback((): string | undefined => {
    if (view === 'analyze') {
      return 'DATA_ANALYTICS';
    } else if (view === 'target') {
      return 'TARGET_BUYER_PROFILE';
    }
    return undefined;
  }, [view]);

  // Helper function to check if last message is assistant and add error message if not
  const ensureLastMessageIsAssistant = useCallback((messages: Message[], includeRetry: boolean = true): { messages: Message[]; shouldAddRetry: boolean } => {
    if (messages.length === 0) return { messages, shouldAddRetry: false };
    
    const lastMessage = messages[messages.length - 1];
    
    // If last message is assistant, return as is
    if (lastMessage.role === 'assistant') {
      return { messages, shouldAddRetry: false };
    }
    
    // If last message is not assistant (user or system), add an assistant message
    logger.warn('Last message is not assistant, adding error message', { 
      lastMessageRole: lastMessage.role,
      totalMessages: messages.length 
    });
    
    // Use same format as network error messages for consistency
    const errorMessage: Message = {
      role: 'assistant',
      content: "I didn't receive a proper response from the server. Please try again. If the problem persists, it may be a temporary server issue.",
      id: `missing_assistant_${Date.now()}_${Math.random()}`,
      timestamp: new Date().toISOString(),
    };
    
    const messagesWithError = [...messages, errorMessage];
    return { 
      messages: messagesWithError, 
      shouldAddRetry: includeRetry && !!lastUserMessageRef.current 
    };
  }, []);

  // Update chat state helper
  const updateChatState = useCallback((updates: Partial<ChatState>) => {
    setChatState((prev) => {
      let processedUpdates = { ...updates };
      
      // If chatHistory is being updated, ensure last message is assistant
      if (updates.chatHistory) {
        const { messages: ensuredMessages, shouldAddRetry } = ensureLastMessageIsAssistant(updates.chatHistory);
        processedUpdates.chatHistory = ensuredMessages;
        
        // Add retry suggestion if needed (override existing suggestions if assistant message was missing)
        if (shouldAddRetry) {
          processedUpdates.suggestions = [{
            id: 'RETRY',
            text: 'Retry',
          }];
        }
        
        chatHistoryRef.current = ensuredMessages;
      }
      
      const newState = { ...prev, ...processedUpdates };
      return newState;
    });
  }, [ensureLastMessageIsAssistant]);

  // Function to call generateEmail API
  const generateEmailFromConversation = useCallback(async (messages: Message[], conversationId: string) => {
    try {
      logger.info('📧 generateEmailFromConversation called', { 
        dealerId: currentDealerId, 
        conversationId, 
        messageCount: messages.length 
      });
      
      if (!currentDealerId || !conversationId) {
        logger.warn('Cannot generate email: missing dealer ID or conversation ID', {
          hasDealerId: !!currentDealerId,
          hasConversationId: !!conversationId
        });
        return;
      }

      if (!messages || messages.length === 0) {
        logger.warn('Cannot generate email: no messages provided');
        return;
      }

      // Reset completion flag when starting new email generation
      emailGenerationCompleteRef.current = false;
      setIsGeneratingEmail(true);
      // Initialize streaming tracking
      lastStreamingUpdateTimeRef.current = Date.now();
      setIsStreamingStalled(false);
      // Auto-open modal only on first email generation start
      if (!hasAutoOpenedModal) {
        setIsEmailGenerationModalOpen(true);
        setHasAutoOpenedModal(true);
      }
      setLoading(true);
      setError('');

      // Convert messages to API format
      const apiMessages = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));

      const emailRequest: GenerateEmailRequest = {
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        messages: apiMessages,
        action: 'GENERATE_EMAIL'
      };

      logger.info('📧 Calling email generation API...', emailRequest);

      // Store last email generation request for retry functionality
      lastEmailGenerationRef.current = { messages, conversationId };

      const abortController = new AbortController();
      const response = await MiaChatV2ApiService.generateEmail(emailRequest, abortController.signal);

      if (abortController.signal.aborted) {
        logger.warn('Email generation API call was aborted');
        emailGenerationCompleteRef.current = true; // Mark as complete (aborted)
        setIsGeneratingEmail(false);
        setIsEmailGenerationModalOpen(false);
        setHasAutoOpenedModal(false);
        setStreamingData(null);
        setCurrentSection(null);
        setIsStreamingStalled(false);
        setLoading(false);
        return;
      }

      logger.info('📧 Email generation API response received', response);

      if (response && response.body) {
        logger.info('Email generated successfully');
        
        // Store the generated email response in the format expected by EmailPreviewModal
        const emailResponse: GenerateEmailResponse = {
          generatedHtml: response.body.generatedHtml || '',
          subject: response.body.emailPreview?.meta?.subject || response.body.subject || '',
          conversation_id: conversationId,
          messages: response.body.messages || [],
          suggestions: response.body.suggestions || [],
          emailPreview: response.body.emailPreview || null,
        };
        
        setGeneratedEmail(emailResponse);
        
        // Update chat history with messages from response
        if (response.body.messages && response.body.messages.length > 0) {
          const displayMessages: Message[] = response.body.messages.map((msg: any, index: number) => {
            const message: Message = {
              role: msg.role,
              content: msg.content,
              id: `email_api_${Date.now()}_${index}`,
            };
            // Add timestamp if it exists in the message
            if (msg.timestamp) {
              (message as any).timestamp = msg.timestamp;
            }
            return message;
          });
          
          updateChatState({
            chatHistory: displayMessages,
            suggestions: response.body.suggestions || [],
            next_action: '',
            is_input_restricted_to_suggestions: false,
            loading: false,
          });
        }
        
        // Email generation complete - hide the progress card
        emailGenerationCompleteRef.current = true; // Mark as complete
        setIsGeneratingEmail(false);
        setIsEmailGenerationModalOpen(false);
        setHasAutoOpenedModal(false); // Reset for next email generation
        setStreamingData(null);
        setCurrentSection(null);
        setIsStreamingStalled(false);
        
        // Email generation will be handled via socket events for streaming
        // The socket will send email_generation and streaming_section_response events
      } else {
        // No response or empty response - treat as error
        const errorMessage: Message = {
          role: 'assistant',
          content: "I received an empty response from the email generation server. Please try again. If the problem persists, it may be a temporary server issue.",
          id: `error_email_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add retry suggestion for email generation
        const retrySuggestion = lastEmailGenerationRef.current ? {
          id: 'RETRY_EMAIL',
          text: 'Retry',
        } : null;
        
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Remove system/processing messages when error occurs
          const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
          return {
            ...prev,
            loading: false,
            chatHistory: [...cleanedHistory, errorMessage],
            suggestions: retrySuggestion ? [retrySuggestion] : [],
          };
        });
        
        // Email generation failed - hide the progress card
        emailGenerationCompleteRef.current = true; // Mark as complete (failed)
        setIsGeneratingEmail(false);
        setIsEmailGenerationModalOpen(false);
        setHasAutoOpenedModal(false);
        setStreamingData(null);
        setCurrentSection(null);
        setIsStreamingStalled(false);
        
        const errorText = 'No response received from email generation server';
        setError(errorText);
        setLocalError(errorText);
      }
    } catch (error) {
      logger.error('Error generating email', error);
      
      // Check if error is due to abort (don't show error for aborted requests)
      if (error instanceof Error && error.name === 'AbortError') {
        setIsGeneratingEmail(false);
        setIsEmailGenerationModalOpen(false);
        setHasAutoOpenedModal(false);
        setLoading(false);
        return;
      }
      
      // Format error message for network issues
      const formattedErrorMessage = formatNetworkErrorMessage(error);
      
      if (formattedErrorMessage) {
        // Add error message to chat history as assistant message
        const errorMessage: Message = {
          role: 'assistant',
          content: formattedErrorMessage,
          id: `error_email_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add retry suggestion for email generation
        const retrySuggestion = lastEmailGenerationRef.current ? {
          id: 'RETRY_EMAIL',
          text: 'Retry',
        } : null;
        
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Remove system/processing messages when error occurs
          const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
          return {
            ...prev,
            loading: false,
            chatHistory: [...cleanedHistory, errorMessage],
            suggestions: retrySuggestion ? [retrySuggestion] : [],
          };
        });
        
        // Email generation error - hide the progress card
        emailGenerationCompleteRef.current = true; // Mark as complete (error)
        setIsGeneratingEmail(false);
        setIsEmailGenerationModalOpen(false);
        setHasAutoOpenedModal(false);
        setStreamingData(null);
        setCurrentSection(null);
        setIsStreamingStalled(false);
        
        const errorMessageText = error instanceof Error ? error.message : 'Failed to generate email';
        setError(errorMessageText);
        setLocalError(errorMessageText);
      } else {
        setIsGeneratingEmail(false);
        setIsEmailGenerationModalOpen(false);
        setHasAutoOpenedModal(false);
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, setLoading, setError]);

  // Monitor streaming timeout and detect when streaming stops
  useEffect(() => {
    if (!isGeneratingEmail) {
      // Clear timeouts and reset state when email generation stops
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
        streamingTimeoutRef.current = null;
      }
      if (streamingCheckIntervalRef.current) {
        clearInterval(streamingCheckIntervalRef.current);
        streamingCheckIntervalRef.current = null;
      }
      setIsStreamingStalled(false);
      lastStreamingUpdateTimeRef.current = null;
      return;
    }

    // Wait time before considering streaming stalled (20 seconds)
    // For testing: reduce to 3000 (3 seconds) - revert to 20000 for production
    const STALL_TIMEOUT = process.env.NODE_ENV === 'development' ? 3000 : 20000;
    // Grace period after socket reconnect (5 seconds)
    // For testing: reduce to 1000 (1 second) - revert to 5000 for production
    const RECONNECT_GRACE_PERIOD = process.env.NODE_ENV === 'development' ? 1000 : 5000;
    
    // Set initial update time if not set
    if (lastStreamingUpdateTimeRef.current === null) {
      lastStreamingUpdateTimeRef.current = Date.now();
    }
    
    // Check for stalling periodically
    streamingCheckIntervalRef.current = setInterval(() => {
      if (!isGeneratingEmail) return;
      
      const now = Date.now();
      const lastUpdate = lastStreamingUpdateTimeRef.current;
      
      // Show engaging content if socket is disconnected (treat as processing)
      if (!isSocketConnected) {
        setIsStreamingStalled(true);
        return;
      }
      
      // Check if streaming has stalled (no updates for timeout period)
      if (lastUpdate && (now - lastUpdate) > STALL_TIMEOUT) {
        // Additional check: if socket just reconnected, give it grace period before showing engaging content
        const timeSinceLastUpdate = now - lastUpdate;
        const isWithinReconnectGrace = timeSinceLastUpdate < (STALL_TIMEOUT + RECONNECT_GRACE_PERIOD);
        
        if (!isWithinReconnectGrace) {
          logger.info('Streaming detected as stalled - no updates for', timeSinceLastUpdate, 'ms');
          setIsStreamingStalled(true);
        } else {
          // Within grace period - keep showing engaging content seamlessly
          setIsStreamingStalled(true);
        }
      } else {
        // Streaming is active
        setIsStreamingStalled(false);
      }
    }, 3000); // Check every 3 seconds
    
    return () => {
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
      }
      if (streamingCheckIntervalRef.current) {
        clearInterval(streamingCheckIntervalRef.current);
      }
    };
  }, [isGeneratingEmail, isSocketConnected]);

  // Subscribe to socket when conversationId is available
  useEffect(() => {
    if (!conversationId || !socketServiceRef.current) {
      return;
    }

    const service = socketServiceRef.current;
    
    // Check if already subscribed to this conversation
    if (service.isSubscribedToConversation(conversationId)) {
      logger.info(`Already subscribed to conversation: ${conversationId}`);
      return;
    }

    // Create callback for socket events
    const handleSocketEvent = (eventData: MiaChatEvent) => {
      try {
        logger.info('Socket event received:', eventData);
        
        // Handle system messages (processing status, tool calls, etc.)
        if (eventData.message && eventData.message.role === 'system') {
          // Get content from message.text (string) or message.text.value (object) or message.content
          let content = '';
          
          if (typeof eventData.message.text === 'string') {
            content = eventData.message.text;
          } else if (eventData.message.text && typeof eventData.message.text === 'object' && 'value' in eventData.message.text) {
            content = eventData.message.text.value;
          } else if (eventData.message.content) {
            content = eventData.message.content;
          }
          
          // Skip if no content
          if (!content) {
            return;
          }
          
          // Add system message to chat history with processingType
          // Add sequentially, don't replace - each message should be unique
          const systemMessage: Message = {
            role: 'system',
            content: content,
            id: `socket_system_${Date.now()}_${Math.random()}`,
            timestamp: eventData.message.timestamp || new Date().toISOString(),
            processingType: eventData.processing_type || eventData.message.processing_type || 'processing',
          };
          
          setChatState((prev) => {
            // Check if this exact message already exists (same content) to avoid duplicates
            const messageExists = prev.chatHistory.some(
              msg => msg.role === 'system' && 
                     msg.processingType === systemMessage.processingType && 
                     msg.content === systemMessage.content
            );
            
            if (messageExists) {
              return prev; // Don't add duplicate
            }
            
            // Add new system message sequentially
            return {
              ...prev,
              chatHistory: [...prev.chatHistory, systemMessage],
              loading: eventData.processing_type === 'processing' || eventData.processing_type === 'generating',
            };
          });
        }
        // Handle assistant/user messages
        else if (eventData.message && (eventData.message.role === 'assistant' || eventData.message.role === 'user')) {
          const newMessage: Message = {
            role: eventData.message.role || 'assistant',
            content: eventData.message.content || '',
            id: `socket_${Date.now()}_${Math.random()}`,
            timestamp: eventData.message.timestamp || new Date().toISOString(),
          };
          
          setChatState((prev) => ({
            ...prev,
            chatHistory: [...prev.chatHistory, newMessage],
            loading: eventData.processing_type === 'processing' || eventData.processing_type === 'generating',
          }));
        }
        // Handle processing_status events (convert to system message)
        else if (eventData.processing_type) {
          // Get content from message.text (string) or message.text.value (object) or message.content
          let content = '';
          
          if (eventData.message) {
            // Check if message.text is a string (direct text)
            if (typeof eventData.message.text === 'string') {
              content = eventData.message.text;
            }
            // Check if message.text is an object with value property
            else if (eventData.message.text && typeof eventData.message.text === 'object' && 'value' in eventData.message.text) {
              content = eventData.message.text.value;
            }
            // Fallback to message.content
            else if (eventData.message.content) {
              content = eventData.message.content;
            }
          }
          
          // Skip if no content (don't add empty messages)
          if (!content) {
            // Only use defaults if we have tool_name
            if (eventData.tool_name) {
              content = `Executing: ${eventData.tool_name}`;
            } else {
              return; // Don't add generic "Processing your request..." messages
            }
          }
          
          const processingMessage: Message = {
            role: 'system',
            content: content,
            id: `socket_processing_${Date.now()}_${Math.random()}`,
            timestamp: eventData.message?.timestamp || eventData.timestamp || new Date().toISOString(),
            processingType: eventData.processing_type,
          };
          
          setChatState((prev) => {
            // Check if this exact message already exists (same content) to avoid duplicates
            const messageExists = prev.chatHistory.some(
              msg => msg.role === 'system' && 
                     msg.processingType === processingMessage.processingType && 
                     msg.content === processingMessage.content
            );
            
            if (messageExists) {
              return prev; // Don't add duplicate
            }
            
            // Add new processing message sequentially (don't replace existing ones)
            return {
              ...prev,
              chatHistory: [...prev.chatHistory, processingMessage],
              loading: eventData.processing_type === 'processing' || eventData.processing_type === 'generating',
            };
          });
        }

        // Update suggestions if available
        if (eventData.suggestions) {
          setChatState((prev) => ({
            ...prev,
            suggestions: eventData.suggestions || [],
            is_input_restricted_to_suggestions: eventData.is_input_restricted_to_suggestions || false,
            next_action: eventData.next_action || prev.next_action,
          }));
        }
        
        // Handle GENERATE_EMAIL action from socket events
        if ((eventData.action === 'GENERATE_EMAIL' || eventData.next_action === 'GENERATE_EMAIL') && conversationId) {
          logger.info('GENERATE_EMAIL action received from socket, triggering email generation API...');
          // Get current chat history from ref (most up-to-date)
          const currentMessages = chatHistoryRef.current.length > 0 
            ? chatHistoryRef.current 
            : chatState.chatHistory;
          
          // Trigger email generation API call
          generateEmailFromConversation(currentMessages, conversationId).catch((error) => {
            logger.error('Error in email generation from socket event', error);
          });
        }

        // Handle email_generation events - only set if we're actually generating email
        // Check for email_content or specific email generation indicators
        // Don't process if email generation is already complete
        if (!emailGenerationCompleteRef.current && (eventData.email_content || (eventData.status === 'processing' && eventData.email_content !== undefined))) {
          setIsGeneratingEmail(true);
          // Initialize streaming tracking
          lastStreamingUpdateTimeRef.current = Date.now();
          setIsStreamingStalled(false);
          // Auto-open modal only on first email generation start (not on every streaming event)
          if (!hasAutoOpenedModal) {
            setIsEmailGenerationModalOpen(true);
            setHasAutoOpenedModal(true);
          }
          setStreamingData(eventData);
          
          // Extract current section from event data if available
          if (eventData.message?.sections && eventData.message.sections.length > 0) {
            // Get the first section or the section being processed
            const section = eventData.message.sections[0];
            setCurrentSection({
              index: section.index || 0,
              type: section.type || '',
              main: section.main,
              sub_text: section.sub_text,
            });
          }
        }
        
        // Handle streaming_section_response events - for email generation streaming
        // Check for streaming_section_response or any streaming events with sections/text
        // Don't process if email generation is already complete
        if (!emailGenerationCompleteRef.current && (eventData.message?.sections || (eventData.message?.text && typeof eventData.message.text === 'object'))) {
          // Process streaming events if:
          // 1. We're already generating email, OR
          // 2. This has email_content, OR
          // 3. This has sections (which indicates email generation)
          if (isGeneratingEmail || eventData.email_content !== undefined || eventData.message?.sections) {
            setIsGeneratingEmail(true);
            
            // Track streaming update time - reset stall state when new data arrives
            lastStreamingUpdateTimeRef.current = Date.now();
            setIsStreamingStalled(false);
            
            // Don't auto-open modal on streaming events - only on first email generation start
            // Modal will only open if user clicks on EmailGenerationProgressCard
            setStreamingData(eventData);
            
            // Update current section from streaming data
            const messageText = eventData.message?.text;
            if (messageText && typeof messageText === 'object' && 'key' in messageText) {
              setCurrentSection({
                index: messageText.key?.index || 0,
                type: messageText.key?.type || '',
                main: messageText.key?.main,
                sub_text: messageText.key?.sub_text,
              });
            } else if (eventData.message?.sections && eventData.message.sections.length > 0) {
              const section = eventData.message.sections[0];
              setCurrentSection({
                index: section.index || 0,
                type: section.type || '',
                main: section.main,
                sub_text: section.sub_text,
              });
            }
          }
        }
        
        // Stop email generation when status is completed or success
        // Also check for isFinal flag in message or streaming data
        const messageIsFinal = (eventData.message as any)?.isFinal === true;
        const streamingDataIsFinal = (streamingData as any)?.message?.isFinal === true;
        const isEmailComplete = eventData.status === 'completed' || 
                                eventData.status === 'success' ||
                                messageIsFinal ||
                                streamingDataIsFinal;
        
        if (isEmailComplete) {
          emailGenerationCompleteRef.current = true;
          setIsGeneratingEmail(false);
          setIsEmailGenerationModalOpen(false);
          setHasAutoOpenedModal(false); // Reset for next email generation
          setCurrentSection(null);
          setIsStreamingStalled(false);
          // Clear streaming data immediately when complete
          setStreamingData(null);
        }
        
        // Also check processing_type for completion
        if (eventData.processing_type === 'completed' && isGeneratingEmail) {
          emailGenerationCompleteRef.current = true;
          setIsGeneratingEmail(false);
          setIsEmailGenerationModalOpen(false);
          setHasAutoOpenedModal(false); // Reset for next email generation
          setCurrentSection(null);
          setIsStreamingStalled(false);
          // Clear streaming data immediately when complete
          setStreamingData(null);
        }

        // Update progress cards if available
        // Note: email_content updates are handled via progress cards, not directly in chat state

        // Update processing status for loading state
        if (eventData.processing_type) {
          setChatState((prev) => ({
            ...prev,
            loading: eventData.processing_type === 'processing' || eventData.processing_type === 'generating',
          }));
        }
        
        // Remove system messages when processing is completed
        if (eventData.processing_type === 'completed' || eventData.status === 'completed') {
          setChatState((prev) => ({
            ...prev,
            chatHistory: prev.chatHistory.filter(msg => !(msg.role === 'system' && msg.processingType)),
            loading: false,
          }));
        }
      } catch (error) {
        logger.error('Error handling socket event', error);
      }
    };

    subscriptionCallbackRef.current = handleSocketEvent;

    // Ensure socket is connected before subscribing
    if (!service.isConnected()) {
      logger.info('Socket not connected, attempting to connect...');
      service.connect();
      
      // Wait for connection, then subscribe
      const connectHandler = () => {
        logger.info('Socket connected, now subscribing...');
        service.subscribeToConversation(conversationId, handleSocketEvent);
        logger.info(`Subscribed to conversation: ${conversationId}`);
        service.off('connect', connectHandler);
      };
      
      service.on('connect', connectHandler);
      
      // Also try subscribing immediately in case it's already connected
      setTimeout(() => {
        if (service.isConnected() && !service.isSubscribedToConversation(conversationId)) {
          service.subscribeToConversation(conversationId, handleSocketEvent);
          logger.info(`Subscribed to conversation (after timeout): ${conversationId}`);
          service.off('connect', connectHandler);
        }
      }, 500);
    } else {
      // Socket is already connected, subscribe immediately
      service.subscribeToConversation(conversationId, handleSocketEvent);
      logger.info(`Subscribed to conversation (socket already connected): ${conversationId}`);
    }

    // Cleanup: unsubscribe when conversationId changes or component unmounts
    return () => {
      if (service && conversationId) {
        service.unsubscribeFromConversation(conversationId);
        logger.info(`Unsubscribed from conversation: ${conversationId}`);
        subscriptionCallbackRef.current = null;
      }
    };
  }, [conversationId]);

  // Auto-initialize and send initial message when provided
  useEffect(() => {
    if (initialMessage && !hasInitializedRef.current && !conversationId && currentDealerId) {
      hasInitializedRef.current = true;
      // Call initializeAndSendMessage directly
      initializeAndSendMessage(initialMessage).catch((error) => {
        logger.error('Error in auto-initialization', error);
      });
    }
    // Reset initialization flag when initialMessage changes
    if (!initialMessage) {
      hasInitializedRef.current = false;
    }
  }, [initialMessage, conversationId, currentDealerId]);

  // Initialize chat and send first message
  const initializeAndSendMessage = useCallback(async (message: string) => {
    try {
      if (!currentDealerId) {
        throw new Error('No dealer selected');
      }

      setIsLoading(true);
      setLocalError(null);
      setLoading(true);
      setError('');

      // Format message for target view: "Target <prompt> shoppers"
      let formattedMessage = message;
      if (view === 'target') {
        // Always format as "Target <prompt> shoppers" for target view
        // Remove any existing "Target" prefix and "shoppers" suffix to avoid duplication
        let cleanMessage = message.trim();
        if (cleanMessage.toLowerCase().startsWith('target')) {
          cleanMessage = cleanMessage.substring(6).trim(); // Remove "Target" prefix
        }
        if (cleanMessage.toLowerCase().endsWith('shoppers')) {
          cleanMessage = cleanMessage.substring(0, cleanMessage.length - 8).trim(); // Remove "shoppers" suffix
        }
        formattedMessage = `Target ${cleanMessage} shoppers`;
      }

      // Add user message to chat history immediately
      const userMessage: Message = {
        role: 'user',
        content: formattedMessage,
        id: `user_${Date.now()}_${Math.random()}`,
        timestamp: new Date().toISOString(),
      };

      // Store last user message for retry functionality
      lastUserMessageRef.current = { text: formattedMessage };

      // Use functional update to ensure we get the latest state
      setChatState((prev) => ({
        ...prev,
        loading: true,
        chatHistory: [...prev.chatHistory, userMessage],
        inputText: '',
        step: 1 as FlowStep,
      }));

      // Initialize chat
      let activeConversationId = conversationId;
      if (!activeConversationId) {
        const initAbortController = new AbortController();
        initChatAbortControllerRef.current = initAbortController;

        const selectedOperation = getSelectedOperation();
        const initResponse = await initChat(selectedOperation, initAbortController.signal);

        if (!initResponse || !initResponse.conversation_id) {
          // Store last user message for retry functionality
          lastUserMessageRef.current = { text: formattedMessage };
          throw new Error('Failed to initialize chat - no conversation ID received');
        }

        activeConversationId = initResponse.conversation_id;
        setConversationId(activeConversationId);
      }

      // For generate view, find operation and use startNewChat
      if (view === 'generate') {
        // Find operation by message text
        // This is a simplified version - you may need to adjust based on your operations structure
        const operationAction = formattedMessage; // Use formatted message

        const startNewChatAbortController = new AbortController();
        const response = await startNewChat(
          operationAction,
          activeConversationId,
          startNewChatAbortController.signal
        );

        if (response && response.messages && response.messages.length > 0) {
          // Use messages from API response - they already include user and assistant messages
          const displayMessages: Message[] = response.messages.map((msg: APIMessage, index: number) => ({
            ...msg,
            id: `api_${Date.now()}_${index}`,
            timestamp: new Date().toISOString(),
          }));

          updateChatState({
            loading: false,
            chatHistory: displayMessages,
            suggestions: response.suggestions || [],
            next_action: response.next_action || '',
            is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false,
          });
          
          // Extract and set progress cards
          if (response.progress_cards && response.progress_cards.length > 0) {
            setProgressCards(response.progress_cards);
          }
          
          // Auto-trigger email generation if next_action is GENERATE_EMAIL
          logger.info('Checking next_action for email generation', { 
            next_action: response.next_action, 
            conversationId: activeConversationId 
          });
          if (response.next_action === 'GENERATE_EMAIL' && activeConversationId) {
            logger.info('✅ Auto-triggering email generation from startNewChat...', {
              next_action: response.next_action,
              conversationId: activeConversationId,
              messageCount: displayMessages.length
            });
            generateEmailFromConversation(displayMessages, activeConversationId).catch((error) => {
              logger.error('Error in auto email generation', error);
            });
          } else {
            logger.info('❌ Email generation not triggered', {
              next_action: response.next_action,
              expected: 'GENERATE_EMAIL',
              hasConversationId: !!activeConversationId
            });
          }
        } else {
          // No response or empty messages - treat as error
          const errorMessage: Message = {
            role: 'assistant',
            content: "I received an empty response from the server. Please try again. If the problem persists, it may be a temporary server issue.",
            id: `error_${Date.now()}_${Math.random()}`,
            timestamp: new Date().toISOString(),
          };
          
          // Add retry suggestion if we have a last user message
          const retrySuggestion = lastUserMessageRef.current ? {
            id: 'RETRY',
            text: 'Retry',
          } : null;
          
          // Use functional update to get latest state
          // Also remove system/processing messages when error occurs
          setChatState((prev) => {
            const latestHistory = prev.chatHistory || [];
            // Remove system/processing messages when error occurs
            const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
            return {
              ...prev,
              loading: false,
              chatHistory: [...cleanedHistory, errorMessage],
              suggestions: retrySuggestion ? [retrySuggestion] : [],
            };
          });
          
          const errorText = 'No response received from server';
          setLocalError(errorText);
          setError(errorText);
        }
      } else {
        // For analyze and target views, use sendChatMessage
        const chatRequest: ChatRequest = {
          dealer_id: currentDealerId,
          conversation_id: activeConversationId,
          messages: [
            {
              role: 'user',
              content: formattedMessage,
            }
          ],
        };

        const chatAbortController = new AbortController();
        const response = await sendChatMessage(chatRequest, chatAbortController.signal);

        // Check if request was aborted
        if (chatAbortController.signal.aborted) {
          updateChatState({
            loading: false,
          });
          setIsLoading(false);
          setLoading(false);
          return;
        }

        if (response && response.messages && response.messages.length > 0) {
          // Use messages from API response - they already include user and assistant messages
          const displayMessages: Message[] = response.messages.map((msg: APIMessage, index: number) => ({
            role: msg.role,
            content: msg.content,
            id: `api_${Date.now()}_${index}`,
            timestamp: new Date().toISOString(),
          }));

          updateChatState({
            loading: false,
            chatHistory: displayMessages,
            suggestions: response.suggestions || [],
            next_action: response.next_action || '',
            is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false,
          });
          
          // Extract and set progress cards
          if (response.progress_cards && response.progress_cards.length > 0) {
            setProgressCards(response.progress_cards);
          }
          
          // Auto-trigger email generation if next_action is GENERATE_EMAIL
          logger.info('Checking next_action for email generation (analyze/target)', { 
            next_action: response.next_action, 
            conversationId: activeConversationId 
          });
          if (response.next_action === 'GENERATE_EMAIL' && activeConversationId) {
            logger.info('✅ Auto-triggering email generation from initializeAndSendMessage...', {
              next_action: response.next_action,
              conversationId: activeConversationId,
              messageCount: displayMessages.length
            });
            generateEmailFromConversation(displayMessages, activeConversationId).catch((error) => {
              logger.error('Error in auto email generation', error);
            });
          } else {
            logger.info('❌ Email generation not triggered (analyze/target)', {
              next_action: response.next_action,
              expected: 'GENERATE_EMAIL',
              hasConversationId: !!activeConversationId
            });
          }
        } else {
          // No response or empty messages - treat as error
          const errorMessage: Message = {
            role: 'assistant',
            content: "I received an empty response from the server. Please try again. If the problem persists, it may be a temporary server issue.",
            id: `error_${Date.now()}_${Math.random()}`,
            timestamp: new Date().toISOString(),
          };
          
          // Add retry suggestion if we have a last user message
          const retrySuggestion = lastUserMessageRef.current ? {
            id: 'RETRY',
            text: 'Retry',
          } : null;
          
          // Use functional update to get latest state
          // Also remove system/processing messages when error occurs
          setChatState((prev) => {
            const latestHistory = prev.chatHistory || [];
            // Remove system/processing messages when error occurs
            const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
            return {
              ...prev,
              loading: false,
              chatHistory: [...cleanedHistory, errorMessage],
              suggestions: retrySuggestion ? [retrySuggestion] : [],
            };
          });
          
          const errorText = 'No response received from server';
          setLocalError(errorText);
          setError(errorText);
        }
      }
    } catch (error) {
      logger.error('Error initializing and sending message', error);
      
      // Check if error is due to abort (don't show error for aborted requests)
      if (error instanceof Error && error.name === 'AbortError') {
        updateChatState({
          loading: false,
        });
        setIsLoading(false);
        setLoading(false);
        return;
      }
      
      // Format error message for network issues
      const formattedErrorMessage = formatNetworkErrorMessage(error);
      
      if (formattedErrorMessage) {
        // Add error message to chat history as assistant message
        const errorMessage: Message = {
          role: 'assistant',
          content: formattedErrorMessage,
          id: `error_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add retry suggestion if we have a last user message
        const retrySuggestion = lastUserMessageRef.current ? {
          id: 'RETRY',
          text: 'Retry',
        } : null;
        
        // Add error message to chat history - use functional update to get latest state
        // Ensure error message is appended after user message by using latest state
        // Also remove any system/processing messages when error occurs
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Remove system/processing messages when error occurs
          const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
          // Make sure error message comes after the last message
          return {
            ...prev,
            loading: false,
            chatHistory: [...cleanedHistory, errorMessage],
            // Add retry suggestion if available
            suggestions: retrySuggestion ? [retrySuggestion] : [],
          };
        });
        
        const errorMessageText = error instanceof Error ? error.message : 'Failed to send message';
        setLocalError(errorMessageText);
        setError(errorMessageText);
      } else {
        // Just update loading state for aborted requests
        updateChatState({
          loading: false,
        });
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [currentDealerId, conversationId, view, initChat, startNewChat, sendChatMessage, getSelectedOperation, updateChatState, setLoading, setError, generateEmailFromConversation]);

  // Handle send message
  const handleSendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading) {
      return;
    }

    // If no conversation exists, initialize and send
    if (!conversationId) {
      await initializeAndSendMessage(message);
      return;
    }

    try {
      setIsLoading(true);
      setLocalError(null);
      setLoading(true);
      setError('');

      // Format message for target view: "Target <prompt> shoppers"
      let formattedMessage = message;
      if (view === 'target') {
        // Always format as "Target <prompt> shoppers" for target view
        // Remove any existing "Target" prefix and "shoppers" suffix to avoid duplication
        let cleanMessage = message.trim();
        if (cleanMessage.toLowerCase().startsWith('target')) {
          cleanMessage = cleanMessage.substring(6).trim(); // Remove "Target" prefix
        }
        if (cleanMessage.toLowerCase().endsWith('shoppers')) {
          cleanMessage = cleanMessage.substring(0, cleanMessage.length - 8).trim(); // Remove "shoppers" suffix
        }
        formattedMessage = `Target ${cleanMessage} shoppers`;
      }

      const userMessage: Message = {
        role: 'user',
        content: formattedMessage,
        id: `user_${Date.now()}_${Math.random()}`,
        timestamp: new Date().toISOString(),
      };

      // Store last user message for retry functionality
      lastUserMessageRef.current = { text: formattedMessage };

      // Use functional update to ensure we get the latest state
      setChatState((prev) => ({
        ...prev,
        loading: true,
        chatHistory: [...prev.chatHistory, userMessage],
        inputText: '',
      }));

      const chatRequest: ChatRequest = {
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        messages: [
          {
            role: 'user',
            content: formattedMessage,
          }
        ],
      };

      const chatAbortController = new AbortController();
      const response = await sendChatMessage(chatRequest, chatAbortController.signal);

      // Check if request was aborted
      if (chatAbortController.signal.aborted) {
        updateChatState({
          loading: false,
        });
        setIsLoading(false);
        setLoading(false);
        return;
      }

      if (response && response.messages && response.messages.length > 0) {
        // Use messages from API response - they already include user and assistant messages
        const displayMessages = response.messages.map((msg, index) => ({
          ...msg,
          id: `api_${Date.now()}_${index}`,
          timestamp: new Date().toISOString(),
        }));

        updateChatState({
          loading: false,
          chatHistory: displayMessages,
          suggestions: response.suggestions || [],
          next_action: response.next_action || '',
          is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false,
        });
        
        // Extract and set progress cards
        if (response.progress_cards && response.progress_cards.length > 0) {
          setProgressCards(response.progress_cards);
        }
        
        // Auto-trigger email generation if next_action is GENERATE_EMAIL
        logger.info('Checking next_action for email generation (handleSendMessage)', { 
          next_action: response.next_action, 
          conversationId 
        });
        if (response.next_action === 'GENERATE_EMAIL' && conversationId) {
          logger.info('✅ Auto-triggering email generation from handleSendMessage...', {
            next_action: response.next_action,
            conversationId,
            messageCount: displayMessages.length
          });
          generateEmailFromConversation(displayMessages, conversationId).catch((error) => {
            logger.error('Error in auto email generation', error);
          });
        } else {
          logger.info('❌ Email generation not triggered (handleSendMessage)', {
            next_action: response.next_action,
            expected: 'GENERATE_EMAIL',
            hasConversationId: !!conversationId
          });
        }
      } else {
        // No response or empty messages - treat as error
        const errorMessage: Message = {
          role: 'assistant',
          content: "I received an empty response from the server. Please try again. If the problem persists, it may be a temporary server issue.",
          id: `error_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add retry suggestion if we have a last user message
        const retrySuggestion = lastUserMessageRef.current ? {
          id: 'RETRY',
          text: 'Retry',
        } : null;
        
        // Use functional update to get latest state
        // Also remove system/processing messages when error occurs
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Remove system/processing messages when error occurs
          const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
          return {
            ...prev,
            loading: false,
            chatHistory: [...cleanedHistory, errorMessage],
            suggestions: retrySuggestion ? [retrySuggestion] : [],
          };
        });
        
        const errorText = 'No response received from server';
        setLocalError(errorText);
        setError(errorText);
      }
    } catch (error) {
      logger.error('Error sending message', error);
      
      // Check if error is due to abort (don't show error for aborted requests)
      if (error instanceof Error && error.name === 'AbortError') {
        updateChatState({
          loading: false,
        });
        setIsLoading(false);
        setLoading(false);
        return;
      }
      
      // Format error message for network issues
      const formattedErrorMessage = formatNetworkErrorMessage(error);
      
      if (formattedErrorMessage) {
        // Add error message to chat history as assistant message
        const errorMessage: Message = {
          role: 'assistant',
          content: formattedErrorMessage,
          id: `error_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add error message to chat history - use functional update to get latest state
        // Ensure error message is appended after user message by using latest state
        // Also remove any system/processing messages when error occurs
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Remove system/processing messages when error occurs
          const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
          // Make sure error message comes after the last message
          return {
            ...prev,
            loading: false,
            chatHistory: [...cleanedHistory, errorMessage],
          };
        });
        
        const errorMessageText = error instanceof Error ? error.message : 'Failed to send message';
        setLocalError(errorMessageText);
        setError(errorMessageText);
      } else {
        // Just update loading state for aborted requests
        updateChatState({
          loading: false,
        });
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [conversationId, currentDealerId, sendChatMessage, isLoading, initializeAndSendMessage, updateChatState, setLoading, setError, generateEmailFromConversation]);

  // Handle email preview open/close
  const openEmailPreview = useCallback(() => {
    setIsEmailPreviewOpen(true);
  }, []);

  const closeEmailPreview = useCallback(() => {
    setIsEmailPreviewOpen(false);
  }, []);

  // Handle email generation progress modal open/close
  const openEmailGenerationProgress = useCallback(() => {
    if (isGeneratingEmail) {
      setIsEmailGenerationModalOpen(true);
    }
  }, [isGeneratingEmail]);

  const closeEmailGenerationProgress = useCallback(() => {
    setIsEmailGenerationModalOpen(false);
    // Don't set isGeneratingEmail to false - email generation is still in progress
    // Only close the modal, the card will remain visible
    // hasAutoOpenedModal remains true to prevent auto-open on streaming events after manual close
  }, []);

  // Handle option select
  const handleOptionSelect = useCallback(async (option: { id: string; text: string }) => {
    // Handle PREVIEW option separately
    if (option.id === 'PREVIEW' || option.id === 'PREVIEW_EMAIL') {
      logger.info('Opening email preview modal');
      openEmailPreview();
      return;
    }

    // Handle RESTART option - reset the conversation and go back to EntryScreen
    if (option.id === 'RESTART' || option.id === 'RESTART_CONVERSATION') {
      logger.info('Restart option clicked, resetting conversation and going back to EntryScreen');
      // First reset the chat state
      handleReset();
      // Then call the callback to reset EntryScreen (which sets chatStarted to false)
      if (onResetCallback) {
        onResetCallback();
      }
      return;
    }

    // Handle RETRY_EMAIL option - resend the last email generation request
    if (option.id === 'RETRY_EMAIL' && lastEmailGenerationRef.current) {
      logger.info('Retry email option clicked, resending last email generation request');
      const lastEmailRequest = lastEmailGenerationRef.current;
      
      // Clear retry suggestion from state
      setChatState((prev) => ({
        ...prev,
        suggestions: [],
      }));
      
      // Retry email generation
      await generateEmailFromConversation(lastEmailRequest.messages, lastEmailRequest.conversationId);
      return;
    }

    // Handle RETRY option - resend the last user message
    if (option.id === 'RETRY' && lastUserMessageRef.current) {
      logger.info('Retry option clicked, resending last user message');
      const lastMessage = lastUserMessageRef.current;
      
      // Clear retry suggestion from state
      setChatState((prev) => ({
        ...prev,
        suggestions: [],
      }));
      
      // If conversation_id doesn't exist, use initializeAndSendMessage (which handles init + chat)
      if (!conversationId) {
        logger.info('No conversation_id found, calling initializeAndSendMessage for retry');
        await initializeAndSendMessage(lastMessage.text);
        return;
      }
      
      // If it was from a suggestion, we need to resend it as a suggestion
      if (lastMessage.optionId && lastMessage.optionId !== 'RETRY') {
        // Create a new option object to avoid infinite recursion
        const retryOption = { id: lastMessage.optionId, text: lastMessage.text };
        // Remove the RETRY handler check temporarily by calling the logic directly
        // We'll manually process it to avoid recursion
        if (isLoading) {
          return;
        }
        
        try {
          setIsLoading(true);
          setLocalError(null);
          setLoading(true);
          setError('');

          const userMessage: Message = {
            role: 'user',
            content: retryOption.text,
            id: `user_${Date.now()}_${Math.random()}`,
            timestamp: new Date().toISOString(),
          };

          // Store last user message for retry functionality
          lastUserMessageRef.current = { text: retryOption.text, optionId: retryOption.id };

          // Use functional update to ensure we get the latest state
          setChatState((prev) => ({
            ...prev,
            loading: true,
            chatHistory: [...prev.chatHistory, userMessage],
            inputText: '',
          }));

          // Format message for target view if needed
          let formattedOptionText = retryOption.text;
          if (view === 'target') {
            let cleanMessage = retryOption.text.trim();
            if (cleanMessage.toLowerCase().startsWith('target')) {
              cleanMessage = cleanMessage.substring(6).trim();
            }
            if (cleanMessage.toLowerCase().endsWith('shoppers')) {
              cleanMessage = cleanMessage.substring(0, cleanMessage.length - 8).trim();
            }
            formattedOptionText = `Target ${cleanMessage} shoppers`;
          }

          const chatRequest: ChatRequest = {
            dealer_id: currentDealerId,
            conversation_id: conversationId,
            messages: [
              {
                role: 'user',
                content: formattedOptionText,
              }
            ],
            selected_option: retryOption.id,
          };

          const chatAbortController = new AbortController();
          const response = await sendChatMessage(chatRequest, chatAbortController.signal);

          // Check if request was aborted
          if (chatAbortController.signal.aborted) {
            updateChatState({
              loading: false,
            });
            setIsLoading(false);
            setLoading(false);
            return;
          }

          if (response && response.messages && response.messages.length > 0) {
            const displayMessages = response.messages.map((msg, index) => ({
              ...msg,
              id: `api_${Date.now()}_${index}`,
              timestamp: new Date().toISOString(),
            }));

            updateChatState({
              loading: false,
              chatHistory: displayMessages,
              suggestions: response.suggestions || [],
              next_action: response.next_action || '',
              is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false,
            });
            
            if (response.progress_cards && response.progress_cards.length > 0) {
              setProgressCards(response.progress_cards);
            }
            
            if (response.next_action === 'GENERATE_EMAIL' && conversationId) {
              generateEmailFromConversation(displayMessages, conversationId).catch((error) => {
                logger.error('Error in auto email generation', error);
              });
            }
          } else {
            // No response - treat as error
            const errorMessage: Message = {
              role: 'assistant',
              content: "I received an empty response from the server. Please try again. If the problem persists, it may be a temporary server issue.",
              id: `error_${Date.now()}_${Math.random()}`,
              timestamp: new Date().toISOString(),
            };
            
            const retrySuggestion = lastUserMessageRef.current ? {
              id: 'RETRY',
              text: 'Retry',
            } : null;
            
            setChatState((prev) => ({
              ...prev,
              loading: false,
              chatHistory: [...prev.chatHistory, errorMessage],
              suggestions: retrySuggestion ? [retrySuggestion] : [],
            }));
            
            const errorText = 'No response received from server';
            setLocalError(errorText);
            setError(errorText);
          }
        } catch (error) {
          logger.error('Error in retry (suggestion)', error);
          
          if (error instanceof Error && error.name === 'AbortError') {
            updateChatState({
              loading: false,
            });
            setIsLoading(false);
            setLoading(false);
            return;
          }
          
          const formattedErrorMessage = formatNetworkErrorMessage(error);
          
          if (formattedErrorMessage) {
            const errorMessage: Message = {
              role: 'assistant',
              content: formattedErrorMessage,
              id: `error_${Date.now()}_${Math.random()}`,
              timestamp: new Date().toISOString(),
            };
            
            const retrySuggestion = lastUserMessageRef.current ? {
              id: 'RETRY',
              text: 'Retry',
            } : null;
            
            setChatState((prev) => {
              const latestHistory = prev.chatHistory || [];
              // Remove system/processing messages when error occurs
              const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
              return {
                ...prev,
                loading: false,
                chatHistory: [...cleanedHistory, errorMessage],
                suggestions: retrySuggestion ? [retrySuggestion] : [],
              };
            });
            
            const errorMessageText = error instanceof Error ? error.message : 'Failed to process selection';
            setLocalError(errorMessageText);
            setError(errorMessageText);
          } else {
            updateChatState({
              loading: false,
            });
          }
        } finally {
          setIsLoading(false);
          setLoading(false);
        }
      } else {
        // If it was from text input, check if conversation_id exists
        // If not, use initializeAndSendMessage (which handles init + chat)
        if (!conversationId) {
          logger.info('No conversation_id found, calling initializeAndSendMessage for retry');
          await initializeAndSendMessage(lastMessage.text);
        } else {
          // If conversation_id exists, call handleSendMessage directly
          await handleSendMessage(lastMessage.text);
        }
      }
      return;
    }

    if (!conversationId || isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      setLocalError(null);
      setLoading(true);
      setError('');

      const userMessage: Message = {
        role: 'user',
        content: option.text,
        id: `user_${Date.now()}_${Math.random()}`,
        timestamp: new Date().toISOString(),
      };

      // Store last user message for retry functionality
      lastUserMessageRef.current = { text: option.text, optionId: option.id };

      // Use functional update to ensure we get the latest state
      setChatState((prev) => ({
        ...prev,
        loading: true,
        chatHistory: [...prev.chatHistory, userMessage],
        inputText: '',
      }));

      // Format message for target view if needed
      let formattedOptionText = option.text;
      if (view === 'target') {
        // Always format as "Target <prompt> shoppers" for target view
        let cleanMessage = option.text.trim();
        if (cleanMessage.toLowerCase().startsWith('target')) {
          cleanMessage = cleanMessage.substring(6).trim();
        }
        if (cleanMessage.toLowerCase().endsWith('shoppers')) {
          cleanMessage = cleanMessage.substring(0, cleanMessage.length - 8).trim();
        }
        formattedOptionText = `Target ${cleanMessage} shoppers`;
      }

      const chatRequest: ChatRequest = {
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        messages: [
          {
            role: 'user',
            content: formattedOptionText,
          }
        ],
        selected_option: option.id,
      };

      const chatAbortController = new AbortController();
      const response = await sendChatMessage(chatRequest, chatAbortController.signal);

      // Check if request was aborted
      if (chatAbortController.signal.aborted) {
        updateChatState({
          loading: false,
        });
        setIsLoading(false);
        setLoading(false);
        return;
      }

      if (response && response.messages && response.messages.length > 0) {
        // Use messages from API response - they already include user and assistant messages
        const displayMessages = response.messages.map((msg, index) => ({
          ...msg,
          id: `api_${Date.now()}_${index}`,
          timestamp: new Date().toISOString(),
        }));

        updateChatState({
          loading: false,
          chatHistory: displayMessages,
          suggestions: response.suggestions || [],
          next_action: response.next_action || '',
          is_input_restricted_to_suggestions: response.is_input_restricted_to_suggestions || false,
        });
        
        // Extract and set progress cards
        if (response.progress_cards && response.progress_cards.length > 0) {
          setProgressCards(response.progress_cards);
        }
        
        // Auto-trigger email generation if next_action is GENERATE_EMAIL
        logger.info('Checking next_action for email generation (handleOptionSelect)', { 
          next_action: response.next_action, 
          conversationId 
        });
        if (response.next_action === 'GENERATE_EMAIL' && conversationId) {
          logger.info('✅ Auto-triggering email generation from handleOptionSelect...', {
            next_action: response.next_action,
            conversationId,
            messageCount: displayMessages.length
          });
          generateEmailFromConversation(displayMessages, conversationId).catch((error) => {
            logger.error('Error in auto email generation', error);
          });
        } else {
          logger.info('❌ Email generation not triggered (handleOptionSelect)', {
            next_action: response.next_action,
            expected: 'GENERATE_EMAIL',
            hasConversationId: !!conversationId
          });
        }
      } else {
        // No response or empty messages - treat as error
        const errorMessage: Message = {
          role: 'assistant',
          content: "I received an empty response from the server. Please try again. If the problem persists, it may be a temporary server issue.",
          id: `error_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add retry suggestion if we have a last user message
        const retrySuggestion = lastUserMessageRef.current ? {
          id: 'RETRY',
          text: 'Retry',
        } : null;
        
        // Use functional update to get latest state
        // Also remove system/processing messages when error occurs
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Remove system/processing messages when error occurs
          const cleanedHistory = latestHistory.filter(msg => !(msg.role === 'system' && msg.processingType));
          return {
            ...prev,
            loading: false,
            chatHistory: [...cleanedHistory, errorMessage],
            suggestions: retrySuggestion ? [retrySuggestion] : [],
          };
        });
        
        const errorText = 'No response received from server';
        setLocalError(errorText);
        setError(errorText);
      }
    } catch (error) {
      logger.error('Error handling option select', error);
      
      // Check if error is due to abort (don't show error for aborted requests)
      if (error instanceof Error && error.name === 'AbortError') {
        updateChatState({
          loading: false,
        });
        setIsLoading(false);
        setLoading(false);
        return;
      }
      
      // Format error message for network issues
      const formattedErrorMessage = formatNetworkErrorMessage(error);
      
      if (formattedErrorMessage) {
        // Add error message to chat history as assistant message
        const errorMessage: Message = {
          role: 'assistant',
          content: formattedErrorMessage,
          id: `error_${Date.now()}_${Math.random()}`,
          timestamp: new Date().toISOString(),
        };
        
        // Add retry suggestion if we have a last user message
        const retrySuggestion = lastUserMessageRef.current ? {
          id: 'RETRY',
          text: 'Retry',
        } : null;
        
        // Add error message to chat history - use functional update to get latest state
        // Ensure error message is appended after user message by using latest state
        setChatState((prev) => {
          const latestHistory = prev.chatHistory || [];
          // Make sure error message comes after the last message
          return {
            ...prev,
            loading: false,
            chatHistory: [...latestHistory, errorMessage],
            // Add retry suggestion if available
            suggestions: retrySuggestion ? [retrySuggestion] : [],
          };
        });
        
        const errorMessageText = error instanceof Error ? error.message : 'Failed to process selection';
        setLocalError(errorMessageText);
        setError(errorMessageText);
      } else {
        // Just update loading state for aborted requests
        updateChatState({
          loading: false,
        });
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  }, [conversationId, currentDealerId, sendChatMessage, isLoading, updateChatState, setLoading, setError, openEmailPreview, view, generateEmailFromConversation, onResetCallback]);

  // Handle reset
  const handleReset = useCallback(() => {
    try {
      // Unsubscribe from socket before resetting
      if (socketServiceRef.current && conversationId) {
        socketServiceRef.current.unsubscribeFromConversation(conversationId);
        logger.info(`Unsubscribed from conversation during reset: ${conversationId}`);
      }
      
      setChatState(createInitialChatState());
      setConversationId(null);
      setLocalError(null);
      setError('');
      setProgressCards([]);
      setIsGeneratingEmail(false);
      setIsEmailGenerationModalOpen(false);
      setHasAutoOpenedModal(false);
      setStreamingData(null);
      setCurrentSection(null);
      setGeneratedEmail(null);
      setIsEmailPreviewOpen(false);
      setIsStreamingStalled(false);
      lastStreamingUpdateTimeRef.current = null;
      emailGenerationCompleteRef.current = false; // Reset completion flag
      subscriptionCallbackRef.current = null;
    } catch (error) {
      logger.error('Error resetting chat', error);
    }
  }, [setError, conversationId]);

  // Update input text
  const updateInputText = useCallback((text: string) => {
    try {
      updateChatState({ inputText: text });
    } catch (error) {
      logger.error('Error updating input text', error);
    }
  }, [updateChatState]);

  return {
    chatState,
    conversationId,
    isLoading,
    error,
    progressCards,
    isGeneratingEmail,
    isEmailGenerationModalOpen,
    streamingData,
    currentSection,
    generatedEmail,
    isEmailPreviewOpen,
    isSocketConnected,
    isStreamingStalled,
    handleSendMessage,
    handleOptionSelect,
    handleReset,
    updateInputText,
    getSelectedOperation,
    openEmailPreview,
    closeEmailPreview,
    openEmailGenerationProgress,
    closeEmailGenerationProgress,
  };
};

